import React, {useEffect, useMemo, useState} from 'react';
import {
  Flex, Popover,
  TableColumnsType
} from "antd";
import {t} from "i18next";
import dayjs, {Dayjs} from "dayjs";
import minMax from 'dayjs/plugin/minMax'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import '../../assets/gant.scss';
import useSWR from "swr";
import urls from "../../api/urls";
import {api} from "../../api/apiProvider";
import {StageType} from "../../api/types";
import IconGantMinus from "../../components/ui/icons/IconGantMinus";
import IconGantPlus from "../../components/ui/icons/IconGantPlus";
import IconGantBranch from "../../components/ui/icons/IconGantBranch";
import IconGantLeaf from "../../components/ui/icons/IconGantLeaf";
import {NavLink} from "react-router-dom";

dayjs.extend(minMax);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

type UserValue = {
  label?: string,
  value?: string,
}

type Model = {
  id: number,
  '@id'?: string,
  name: string,
}

interface BriefFilter {
  company?: UserValue,
  pool?: UserValue,
  products?: UserValue[],
  period?: [Dayjs | null, Dayjs | null] | null
}

type Product = {
  company: Company,
  id: number,
  mpmId: string,
  name: string,
}

type Company = {
  '@id': string,
  id: number,
  name: string,
  pool: Model,
}

type Stage = {
  '@id': string,
  id: number,
  name: string,
  dateEnd: string,
  dateEndPlan: string,
  stageType: string,
  order: number,
  isAccepted: boolean,
}

export type DataType = {
  '@id': string,
  name: string,
  version: string,
  id: number,
  products: Product[],
  kpi: Model,
  stages: Stage[],
}

type Brief = {
  id: number,
  name: string,
  stages: Stage[],
  version: string,
};

type Tree = {
  [poolId: number]: {
    id: number;
    name: string;
    companies: {
      [companyId: number]: {
        id: number;
        name: string;
        products: {
          id: number;
          name: string;
          kpis: {
            id: number;
            name: string;
            briefs: Brief[];
          }[];
        }[];
      };
    };
  };
}

type PeriodDay = {
  date: Dayjs;
  day: string,
  text: string
}

type PeriodMonth = {
  date: Dayjs;
  daysCount: number,
  text: string
}

type TableItemValue = {
  text: string,
  shortText: string,
  daysCount: number,
  state: State,
  stateDate: StateDate,
  diff?: number,
  startDate: string,
  endDate: string,
}

type TableItem = {
  name: string,
  key: string,
  id: string,
  [key: `${number}-${number}-${number}`]: TableItemValue | string | number | Dayjs,
  type: TreeItem,
  startDate: string,
  endDate: string,
}

export enum State {
  NotStarted = 'not-started', //Планируемые этапы
  InWork = 'in-work', //Этапы текущего дня
  Completed = 'completed', //Завершенные
  Empty = 'empty', //
}

export enum StateDate {
  Overdue = 'overdue', //с просрочкой
  Advance = 'advance', //с опережением
  Plan = 'plan', //обычные
  Empty = 'empty', //
}


const dateFormat = 'YYYY-MM-DD';

type Result = {
  date: Dayjs,
  dateEnd: Dayjs,
  daysCount: number,
  stage?: Stage,
  isEmpty: boolean,
  diff?: number,
}

type StageName = {
  text: string,
  shortText: string,
}

enum TreeItem {
  Root,
  Branch,
  Leaf,
}

function buildTree(data: DataType[]): Tree {
  const tree: Tree = {};

  data.forEach(item => {
    if (item.products && item.products.length > 0 && item.kpi) {
      const product = item.products[0];
      const stages = item.stages;
      const {id: companyId, name: companyName, pool} = product.company;
      const {id: poolId, name: poolName} = pool;
      const {id: kpiId, name: kpiName} = item.kpi;

      if (!tree[poolId]) {
        tree[poolId] = {
          id: poolId,
          name: poolName,
          companies: {}
        };
      }

      if (!tree[poolId].companies[companyId]) {
        tree[poolId].companies[companyId] = {
          id: companyId,
          name: companyName,
          products: []
        };
      }

      let productNode = tree[poolId].companies[companyId].products.find(
        p => p.id === product.id
      );

      if (!productNode) {
        productNode = {
          id: product.id,
          name: product.name,
          kpis: []
        };
        tree[poolId].companies[companyId].products.push(productNode);
      }

      let kpiNode = productNode.kpis.find(k => k.id === kpiId);

      if (!kpiNode) {
        kpiNode = {
          id: kpiId,
          name: kpiName,
          briefs: []
        };
        productNode.kpis.push(kpiNode);
      }

      kpiNode.briefs.push({
        id: item.id,
        name: kpiName,
        version: item.version,
        stages,
      });
    }
  });

  for (let itemId in tree) {
    const item = tree[itemId];
    if (item.companies) {
      for (let companyId in item.companies) {
        const company = item.companies[companyId];
        if (company.products) {
          for (let companyId in company.products) {
            const product = company.products[companyId];
            if (product.kpis) {
              for (let kpiId in product.kpis) {
                const kpi = product.kpis[kpiId];
                if (kpi.briefs) {
                  kpi.briefs.sort((a: Brief, b: Brief) => Number(a.version) - Number(b.version))
                }
              }
            }
          }
        }
      }
    }
  }

  return tree;
}

function findDateRange(data: DataType[]): { minDate: Dayjs | null; maxDate: Dayjs | null } {
  const allDates: string[] = [];

  // Извлекаем все значения поля dateEnd из массива
  data.forEach(item => {
    item.stages.forEach(stage => {
      if (stage.dateEnd) {
        allDates.push(stage.dateEnd);
      }
    });
  });

  if (allDates.length === 0) {
    return {minDate: null, maxDate: null}; // Если дат нет, возвращаем null
  }

  // Преобразуем строки в объекты dayjs и находим минимальную и максимальную дату
  const minDate = dayjs.min(allDates.map(date => dayjs(date)));
  const maxDate = dayjs.max(allDates.map(date => dayjs(date)));

  return {minDate, maxDate};
}

function generateDateRange(startDate: Dayjs, endDate: Dayjs): PeriodDay[] {
  const result: PeriodDay[] = [];
  let currentDate = startDate;

  while (currentDate.isSameOrBefore(endDate, "day")) {
    result.push({
      date: currentDate,
      day: currentDate.format("YYYY-MM-DD"),
      text: currentDate.format('DD')
    });
    currentDate = currentDate.add(1, "day");
  }

  return result;
}

function capitalizeFirstLetter(val: string) {
  return String(val).charAt(0).toUpperCase() + String(val).slice(1);
}

function calculateStages(
  briefStages: Stage[],
  minDate: Dayjs | undefined,
  maxDate: Dayjs | undefined
): Result[] {
  const result: Result[] = [];

  if (briefStages.length === 0 || !minDate || !maxDate) {
    return result;
  }

  let stagePeriods: Result[] = [];

  for (let i = 0; i < briefStages.length; i++) {
    const currentStage = briefStages[i];

    if (i === 0) {
      let diff = dayjs(currentStage.dateEnd).diff(dayjs(currentStage.dateEndPlan), 'day');
      stagePeriods.push({
        date: dayjs(currentStage.dateEnd),
        dateEnd: dayjs(currentStage.dateEnd),
        stage: currentStage,
        isEmpty: false,
        daysCount: 0,
        diff: diff,
      });

      const nextStage = briefStages[i + 1];
      diff = dayjs(nextStage.dateEnd).diff(dayjs(nextStage.dateEndPlan), 'day');
      stagePeriods.push({
        date: dayjs(currentStage.dateEnd).add(1, 'day'),
        dateEnd: dayjs(nextStage.dateEnd),
        stage: nextStage,
        isEmpty: false,
        daysCount: 0,
        diff,
      })
    } else if (i !== briefStages.length - 1) {
      const nextStage = briefStages[i + 1];
      const diff = dayjs(nextStage.dateEnd).diff(dayjs(nextStage.dateEndPlan), 'day');
      stagePeriods.push({
        date: dayjs(currentStage.dateEnd).add(1, 'day'),
        dateEnd: dayjs(nextStage.dateEnd),
        stage: nextStage,
        isEmpty: false,
        daysCount: 0,
        diff,
      })
    }
  }

  stagePeriods = stagePeriods.filter((period) => {
    return period.dateEnd >= minDate && period.dateEnd <= maxDate
  });

  for (let period of stagePeriods) {
    if (period.date < minDate) {
      period.date = minDate
    }

    if (period.dateEnd > maxDate) {
      period.dateEnd = maxDate
    }

    period.daysCount = period.dateEnd.diff(period.date, "day") + 1
  }

  const firstStage = stagePeriods[0];

  if (firstStage && firstStage.date.isAfter(minDate)) {
    const endDate = firstStage.date.add(-1, 'day');
    stagePeriods.unshift({
      date: minDate,
      dateEnd: endDate,
      isEmpty: true,
      daysCount: endDate.diff(minDate, "day") + 1,
      diff: firstStage.diff,
    })
  }

  const lastStage = stagePeriods[stagePeriods.length - 1];
  if (lastStage && lastStage.dateEnd.isBefore(maxDate)) {
    const startDate = lastStage.dateEnd.add(1, 'day');
    stagePeriods.push({
      date: startDate,
      dateEnd: maxDate,
      isEmpty: true,
      daysCount: maxDate.diff(startDate, "day") + 1,
      diff: lastStage.diff,
    })
  }
  return stagePeriods
}

function generateMonthArrayWithDaysCount(
  dates: { date: Dayjs; day: string }[]
): PeriodMonth[] {
  const monthMap = new Map<string, { date: Dayjs; days: Set<string> }>();

  dates.forEach(({date}) => {
    const monthKey = date.format("YYYY-MM");
    if (!monthMap.has(monthKey)) {
      monthMap.set(monthKey, {date: date.startOf("month"), days: new Set()});
    }
    monthMap.get(monthKey)?.days.add(date.format("YYYY-MM-DD"));
  });

  return Array.from(monthMap.values()).map(({date, days}) => ({
    date,
    text: capitalizeFirstLetter(date.format("MMMM YYYY")),
    daysCount: days.size
  }));
}

const getState = (stage: Result, stages: Stage[]) => {
  if (stage.isEmpty) {
    return State.Empty
  }

  if (dayjs(stage.dateEnd).isBefore(dayjs(), 'day')) {
    return State.Completed
  }

  const currentStage = stages.find((stage: Stage) => dayjs(stage.dateEnd).isAfter(dayjs()) || dayjs(stage.dateEnd).isSame(dayjs(), 'day'));

  if (currentStage && currentStage.id === stage?.stage?.id) {
    return State.InWork
  }

  return State.NotStarted
};

const getStateDate = (stage: Result) => {
  if (stage.isEmpty) {
    return StateDate.Empty
  }

  if (dayjs(stage.dateEnd).isBefore(dayjs(stage?.stage?.dateEndPlan))) {
    return StateDate.Advance
  }

  if (dayjs(stage.dateEnd).isAfter(dayjs(stage?.stage?.dateEndPlan))) {
    return StateDate.Overdue
  }

  return StateDate.Plan
};

const getCellClass = (cell: TableItemValue) => {
  if (cell.state === State.Empty) {
    return cell.state
  }

  const classes: string[] = [];
  classes.push(cell.state);
  classes.push(cell.stateDate);
  return classes.join(' ')
};

function getInitials(text: string) {
  if (!text || typeof text !== "string") return "";

  const words = text.trim().split(/\s+/);
  const initials = words.map(word => word.charAt(0).toUpperCase());

  return initials.join("");
}

export default function useTableData(briefs: DataType[] | undefined,
                                     filterMinDate: Dayjs | null | undefined,
                                     filterMaxDate: Dayjs | null | undefined,
) {
  const [minDate, setMinDate] = useState<Dayjs>();
  const [maxDate, setMaxDate] = useState<Dayjs>();
  const [periodDays, setPeriodDays] = useState<PeriodDay[]>([]);
  const [periodMonths, setPeriodMonths] = useState<PeriodMonth[]>([]);
  const [tree, setTree] = useState<Tree>({});
  const [expandedPools, setExpandedPools] = useState<string[]>([]);
  const [hoveredStage, setHoveredStage] = useState<TableItemValue | null>(null);

  const {
    data: stageTypes,
    mutate: mutateBriefsStageTypes,
  } = useSWR<{
    data: StageType[],
    total: Number,
  }>(`${urls.stageTypes}`, api.get, {
    revalidateOnFocus: false
  });

  useEffect(() => {
    if (briefs) {
      const tree = buildTree(briefs);

      setTree(tree);

      let stagesMinDate, stagesMaxDate;

      if (filterMinDate && filterMaxDate) {
        stagesMinDate = filterMinDate;
        stagesMaxDate = filterMaxDate
      } else {
        let {minDate, maxDate} = findDateRange(briefs);
        if (minDate && maxDate) {
          stagesMinDate = minDate;
          stagesMaxDate = maxDate
        }
      }

      if (stagesMinDate && stagesMaxDate) {
        setMinDate(stagesMinDate);
        setMaxDate(stagesMaxDate);

        const range = generateDateRange(stagesMinDate, stagesMaxDate);
        setPeriodDays(range);
        setPeriodMonths(generateMonthArrayWithDaysCount(range))
      }
    }
  }, [briefs, filterMinDate, filterMaxDate]);

  const columns: TableColumnsType<TableItem> = useMemo(() => {
    const columns: TableColumnsType<TableItem> = [
      /*{
        title: '#',
        dataIndex: 'key',
        key: 'id',
        width: 70,
        fixed: 'left',
      },*/
      {
        title: t('gant.model'),
        dataIndex: 'name',
        key: 'name',
        width: 200,
        fixed: 'left',
        render: (value: TableItemValue, record: TableItem, index: number) => {
          return <Flex className={'gant-table__name'} gap={10} align={'center'}>
            <Flex className={'gant-table__name-icon'} onClick={() => {
              if (record.type !== TreeItem.Root) {
                return
              }

              togglePool(record.id)
            }}>{getIcon(record)}</Flex>
            <div style={{paddingTop: '2px'}}>{
              record.type === TreeItem.Leaf ?
                <NavLink to={`/briefs/${record.id.split('_').pop()}/description`} target={'_blank'}>
                  {record.name}
                </NavLink> : <div className={'gant-table__name-root'}>{record.name}</div>
            }</div>
          </Flex>
        },
      },
    ];

    for (let month of periodMonths) {
      const monthItem: { title: string; dataIndex: string; key: string; width: number; children: any[] } = {
        title: month.text,
        dataIndex: month.date.format(dateFormat),
        key: month.date.format(dateFormat),
        width: 25 * month.daysCount,
        children: [],
      };

      const monthDays = periodDays.filter((day: PeriodDay) => (day.date.month() === month.date.month())
        && (day.date.year() === month.date.year()));

      if (monthDays.length > 0) {
        const today = dayjs();

        for (let day of monthDays) {
          const isWeekend = [0, 6].includes(day.date.day());
          const isCurrent = day.date.isSame(today, 'day');
          const formattedDate = day.date.format(dateFormat);

          const isHovered = hoveredStage && day.date.isSameOrAfter(dayjs(hoveredStage.startDate, t('common.dateFormat')))
            && day.date.isSameOrBefore(dayjs(hoveredStage.endDate, t('common.dateFormat')));

          monthItem.children.push({
            title: <div
              className={'gant-table__day' + (isWeekend ? ' weekend' : '') + (isCurrent ? ' current' : '')
                + (isHovered ? ' hovered' : '')}>{day.text}</div>,
            dataIndex: formattedDate,
            key: formattedDate,
            width: 25,
            render: (value: TableItemValue, record: TableItem, index: number) => {
              return value?.startDate && value?.endDate && value?.text
                ?
                <Popover
                  placement="top"
                  content={
                    <div>
                      <div>{value.startDate} <span className={'gant-table__arrow'}>→</span> {value.endDate}</div>
                      <div>{value?.text}</div>
                    </div>
                  }
                  arrow
                  overlayClassName={'sider-links__popover'}
                >
                  <div
                    className={'gant-table__cell ' + (value && getCellClass(value))}
                    onMouseEnter={() => {
                      setHoveredStage(value);
                    }}
                    onMouseLeave={() => {
                      setHoveredStage(null);
                    }}
                  >{value?.shortText}</div>
                </Popover>
                :
                <div
                  className={'gant-table__cell ' + (value && getCellClass(value))}
                >{value?.shortText}</div>
            },
            onCell: (record: TableItem, index: number) => {
              const itemDate: TableItemValue = record[day.day as `${number}-${number}-${number}`] as TableItemValue;

              return {
                colSpan: (itemDate && itemDate.daysCount) ? itemDate.daysCount : 0,
              }
            },
          })
        }
      }

      columns.push(monthItem)
    }

    return columns
  }, [periodMonths, periodDays, expandedPools, hoveredStage]);
  const getEmptyItem = (name: string) => {
    return {
      text: name,
      shortText: name,
      daysCount: periodDays.length,
      state: State.Empty,
      stateDate: StateDate.Empty,
      startDate: '',
      endDate: '',
    }
  };

  const getStageTypeName = (iri: string): StageName => {
    if (!stageTypes || !stageTypes.data || stageTypes.data.length === 0) {
      return {
        text: '',
        shortText: '',
      }
    }

    const stageType = stageTypes.data.find((type: StageType) => type["@id"] === iri);

    if (!stageType) {
      return {
        text: '',
        shortText: '',
      }
    }

    const isFirst = stageType.id === stageTypes.data[0].id;

    if (isFirst) {
      const name = stageType.name;
      return {
        text: name,
        shortText: getInitials(name),
      }
    }

    return {
      text: stageType.name,
      shortText: stageType.name,
    }
  };

  const isPoolExpanded = (poolId: string) => {
    return expandedPools.includes(poolId)
  };

  const togglePool = (poolId: string) => {
    if (!expandedPools.includes(poolId)) {
      setExpandedPools([
        ...expandedPools,
        poolId,
      ])
    } else {
      setExpandedPools(expandedPools.filter((expandedPoolId: string) => expandedPoolId !== poolId))
    }
  };

  const getIcon = (item: TableItem) => {
    switch (item.type) {
      case TreeItem.Root:
        return isPoolExpanded(item.id) ? <IconGantMinus/> : <IconGantPlus/>;

      case TreeItem.Branch:
        return <IconGantBranch/>;

      case TreeItem.Leaf:
        return <IconGantLeaf/>;

      default:
        return <></>
    }
  };

  const data = useMemo(() => {
    const data: TableItem[] = [];

    if (minDate) {
      const minDateKey = minDate.format(dateFormat).toString() as `${number}-${number}-${number}`;

      let poolNumber = 1;

      for (let itemId in tree) {
        const item = tree[itemId];

        const count = item.companies ? Object.values(item.companies).length : 0;

        data.push({
          name: `${item.name}` + (count ? ` (${String(count)})` : ''),
          key: String(poolNumber),
          id: `group_${item.id}`,
          [minDateKey]: getEmptyItem(item.name),
          type: TreeItem.Root,
          startDate: '',
          endDate: '',
        });

        if (!expandedPools.includes(`group_${item.id}`)) {
          continue
        }

        if (item.companies) {
          let companyNumber = 1;
          for (let companyId in item.companies) {
            const company = item.companies[companyId];
            const count = company.products ? Object.values(company.products).length : 0;

            data.push({
              name: `${company.name}` + (count ? ` (${String(count)})` : ''),
              key: `${poolNumber}.${companyNumber}`,
              id: `company_${company.id}`,
              [minDateKey]: getEmptyItem(company.name),
              type: TreeItem.Branch,
              startDate: '',
              endDate: '',
            });

            if (company.products) {
              let productNumber = 1;
              for (let companyId in company.products) {
                const product = company.products[companyId];
                const count = product.kpis ? Object.values(product.kpis).length : 0;

                data.push({
                  name: `${product.name}` + (count ? ` (${String(count)})` : ''),
                  key: `${poolNumber}.${companyNumber}.${productNumber}`,
                  id: `product_${product.id}`,
                  [minDateKey]: getEmptyItem(product.name),
                  type: TreeItem.Branch,
                  startDate: '',
                  endDate: '',
                });

                if (product.kpis) {
                  let kpiNumber = 1;
                  for (let kpiId in product.kpis) {
                    const kpi = product.kpis[kpiId];
                    if (kpi.briefs.length > 0) {
                      let briefNumber = 1;
                      for (let brief of kpi.briefs) {
                        const stagesDates = calculateStages(brief.stages, minDate, maxDate);
                        const briefData: TableItem = {
                          name: `${kpi.name} (${brief.version})`,
                          key: `${poolNumber}.${companyNumber}.${productNumber}.${kpiNumber}.${briefNumber}`,
                          id: `brief_${brief.id}`,
                          type: TreeItem.Leaf,
                          startDate: '',
                          endDate: '',
                        };

                        briefNumber++;

                        for (let stageDate of stagesDates) {
                          const text = stageDate.isEmpty ? '' : getStageTypeName(String(stageDate?.stage?.stageType)).text;
                          const shortText = stageDate.isEmpty ? '' : getStageTypeName(String(stageDate?.stage?.stageType)).shortText;

                          briefData[stageDate.date.format(dateFormat) as `${number}-${number}-${number}`] = {
                            text: text,
                            shortText: shortText,
                            daysCount: stageDate.daysCount,
                            state: getState(stageDate, brief.stages),
                            stateDate: getStateDate(stageDate),
                            diff: stageDate.diff,
                            startDate: stageDate.date.format(t('common.dateFormat')),
                            endDate: stageDate.dateEnd.format(t('common.dateFormat')),
                          }
                        }

                        data.push(briefData)
                      }
                    }

                    kpiNumber++;
                  }
                }

                productNumber++;
              }
            }

            companyNumber++;
          }
        }

        poolNumber++;
      }
    }

    return data
  }, [tree, expandedPools]);

  return {columns, data}
}
